import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, ShieldCheck, LineChart, LogIn, Home, Menu, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAssessmentStatus } from "@/hooks/useAssessmentStatus";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import ppuLogo from "@/assets/ppu-logo.png";

const Header = () => {
    const navigate = useNavigate();
    const { user, profile, isAdmin, logout } = useAuth();
    const { activeRun, latestAttempt, loading } = useAssessmentStatus();

    const NavItems = ({ mobile = false }) => {
        const items = [
            { label: "Home", path: "/", icon: Home },
            { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
            { label: "Profile", path: "/profile", icon: Users },
            { label: "Analytics", path: "/analytics", icon: LineChart },
        ];

        if (isAdmin) {
            items.push({ label: "Admin", path: "/admin", icon: ShieldCheck });
        }

        return (
            <div className={`flex ${mobile ? "flex-col gap-4 mt-8" : "items-center gap-3"}`}>
                {items.map((item) => (
                    <Button
                        key={item.path}
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(item.path)}
                        className={`gap-2 ${!mobile ? "hidden lg:flex" : "justify-start w-full text-lg py-6"}`}
                    >
                        <item.icon className={mobile ? "h-5 w-5" : "h-4 w-4"} />
                        <span>{item.label}</span>
                    </Button>
                ))}
            </div>
        );
    };

    return (
        <div className="bg-card border-b border-border shadow-sm sticky top-0 z-50">
            <div className="container mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
                        <img src={ppuLogo} alt="PPU Logo" className="h-12 w-12 md:h-16 md:w-16 object-contain" />
                        <div className="flex flex-col">
                            <h1 className="text-sm md:text-lg font-bold text-foreground leading-tight">Palestine Polytechnic University</h1>
                            <p className="text-xs md:text-sm text-muted-foreground font-tajawal leading-tight" dir="rtl">جامعة بوليتكنك فلسطين</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {user ? (
                            <>
                                {/* Desktop Navigation */}
                                <div className="hidden md:flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground mr-2 hidden xl:inline-block">
                                        <span className="font-bold">Welcome,</span> <span className="font-bold text-foreground">{profile?.first_name || user.email}</span>
                                    </span>
                                    <NavItems />
                                    <Button variant="ghost" size="sm" onClick={logout} className="ml-2 text-destructive hover:text-destructive hover:bg-destructive/10">
                                        <LogOut className="h-4 w-4 md:mr-2" />
                                        <span className="hidden md:inline">Logout</span>
                                    </Button>
                                </div>

                                {/* Mobile Navigation */}
                                <div className="flex md:hidden items-center gap-2">
                                    <Sheet>
                                        <SheetTrigger asChild>
                                            <Button variant="outline" size="icon">
                                                <Menu className="h-5 w-5" />
                                            </Button>
                                        </SheetTrigger>
                                        <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                                            <SheetHeader className="text-left border-b pb-4 mb-4">
                                                <SheetTitle className="flex items-center gap-3">
                                                    <img src={ppuLogo} alt="PPU Logo" className="h-10 w-10 object-contain" />
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold truncate">PPU DigComp</span>
                                                        <span className="text-xs text-muted-foreground truncate">{profile?.first_name || user.email}</span>
                                                    </div>
                                                </SheetTitle>
                                            </SheetHeader>
                                            <NavItems mobile />
                                            <div className="mt-8 pt-6 border-t">
                                                <Button variant="destructive" className="w-full justify-start gap-3 text-lg py-6" onClick={logout}>
                                                    <LogOut className="h-5 w-5" />
                                                    Logout
                                                </Button>
                                            </div>
                                        </SheetContent>
                                    </Sheet>
                                </div>
                            </>
                        ) : (
                            <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="gap-2">
                                <LogIn className="h-4 w-4" />
                                <span>Login</span>
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Header;
